import { WorkspaceCoreValue } from '@/common/types/databaseTypes';
import { Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddWorkspaceCoreValue } from '../../services/useWorkspaceCoreValueQueries';
import { CoreValuePill } from '../CoreValuePill';
import { useAuthStore } from '@/stores/authStore';
import { hasEditPermission } from '@/common/constants/member';
import { useCurrentWorkspace } from '../../hooks';

interface CoreValueSettingsModalProps {
    opened: boolean;
    onClose: () => void;
    workspaceId: string;
    coreValues: WorkspaceCoreValue[];
    onSuccess?: () => void; // 작업 성공 시 호출할 콜백 함수
}

/**
 * 핵심 가치 설정 모달 컴포넌트
 * 핵심 가치 추가 및 관리를 위한 재사용 가능한 모달
 */
const CoreValueSettingsModal: FC<CoreValueSettingsModalProps> = ({
    opened,
    onClose,
    workspaceId,
    coreValues,
    onSuccess,
}) => {
    const { t } = useTranslation('feedback');
    const [newCoreValue, setNewCoreValue] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const { user } = useAuthStore();
    const { currentWorkspace } = useCurrentWorkspace(user?.id || undefined);
    const canManageCoreValues = hasEditPermission(
        user?.id || '',
        currentWorkspace?.workspace_member || []
    );

    // 로컬에서 핵심 가치 목록을 관리하여 즉시 UI 갱신
    const [localCoreValues, setLocalCoreValues] = useState<
        WorkspaceCoreValue[]
    >([]);

    // coreValues prop이 변경되면 로컬 상태 업데이트
    useEffect(() => {
        setLocalCoreValues(coreValues);
    }, [coreValues]);

    // 핵심 가치 관련 훅 사용
    const { mutateAsync: addCoreValue, isPending: isAddingCoreValue } =
        useAddWorkspaceCoreValue();

    // 핵심 가치 추가 핸들러
    const handleAddCoreValue = async () => {
        // 이미 처리 중이면 중복 실행 방지
        if (isProcessing) {
            return;
        }

        const trimmedValue = newCoreValue.trim();

        // 값이 비어있거나 작업공간 ID가 없으면 처리하지 않음
        if (trimmedValue === '' || !workspaceId) {
            setIsProcessing(false);
            return;
        }

        // 이미 존재하는 핵심 가치인지 확인
        if (coreValues.some(cv => cv.name === trimmedValue)) {
            notifications.show({
                title: t('setting.coreValue.addError'),
                message: t('setting.coreValue.addErrorMessage', {
                    value: trimmedValue,
                }),
                color: 'red',
            });
            setIsProcessing(false);
            return;
        }

        // 핵심 가치 추가 API 호출
        await addCoreValue(
            {
                workspaceId,
                name: trimmedValue,
            },
            {
                onSuccess: (newCoreValue: WorkspaceCoreValue) => {
                    setIsProcessing(false);
                    setNewCoreValue('');
                    notifications.show({
                        title: t('setting.coreValue.addSuccess'),
                        message: t('setting.coreValue.addSuccessMessage', {
                            value: trimmedValue,
                        }),
                        color: 'green',
                    });
                    // 로컬 상태에 새 핵심 가치 추가하여 UI 즉시 갱신
                    setLocalCoreValues(current => [...current, newCoreValue]);
                    // 성공 콜백 호출
                    onSuccess?.();
                },
                onError: error => {
                    notifications.show({
                        title: t('setting.coreValue.addError'),
                        message: error.message.includes(
                            'uq_workspace_core_value_workspace_name'
                        )
                            ? t('setting.coreValue.addErrorMessage', {
                                  value: trimmedValue,
                              })
                            : error.message,
                        color: 'red',
                    });
                    setIsProcessing(false);
                },
            }
        );
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t('setting.coreValue.setup')}
            centered
            radius="md"
        >
            <Stack>
                <Text size="sm" fw={600}>
                    {t('setting.coreValue.addTitle')}
                </Text>
                <Group>
                    <TextInput
                        placeholder={t('setting.coreValue.placeholder')}
                        value={newCoreValue}
                        onChange={e => setNewCoreValue(e.target.value)}
                        style={{ flex: 1 }}
                        onKeyDown={e => {
                            // Enter 키를 누를 때 중복 실행 방지를 위한 개선된 로직
                            if (e.key === 'Enter') {
                                e.preventDefault(); // 페이지 새로고침 방지

                                // 이미 처리 중이면 중복 실행하지 않음
                                if (!isProcessing) {
                                    setIsProcessing(true); // 상태 먼저 변경
                                    // 마이크로태스크를 통해 비동기 함수 호출 순서 보장
                                    setTimeout(() => {
                                        void handleAddCoreValue();
                                    }, 0);
                                }
                            }
                        }}
                    />
                    <Button
                        variant="filled"
                        size="sm"
                        radius="md"
                        onClick={e => {
                            e.preventDefault();
                            // 이미 처리 중이면 중복 실행하지 않음
                            if (!isProcessing) {
                                setIsProcessing(true); // 상태 먼저 변경
                                // 마이크로태스크를 통해 비동기 함수 호출 순서 보장
                                setTimeout(() => {
                                    void handleAddCoreValue();
                                }, 0);
                            }
                        }}
                        disabled={isProcessing}
                    >
                        {t('setting.coreValue.addButton')}
                    </Button>
                </Group>
                <Group gap={4}>
                    {localCoreValues.map(coreValue => (
                        <CoreValuePill
                            key={coreValue.id}
                            coreValue={coreValue}
                            workspaceId={workspaceId}
                            onSuccess={() => {
                                // 로컬 상태에서 삭제된 항목 즉시 제거
                                setLocalCoreValues(current =>
                                    current.filter(cv => cv.id !== coreValue.id)
                                );
                                // 부모 컴포넌트에도 알림
                                onSuccess?.();
                            }}
                            disabled={isAddingCoreValue}
                            isEditable={canManageCoreValues}
                        />
                    ))}
                </Group>
            </Stack>
        </Modal>
    );
};

export default CoreValueSettingsModal;
